import React from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Clock, X } from 'lucide-react';

/**
 * Iter-46aj — Post-booking promotion modal.
 *
 * Shown after a client successfully books a class. Lists other classes
 * with the same name across the upcoming weeks and lets them add several
 * to a basket + check out in one transaction (taking advantage of the
 * card the SDK has already verified for this session).
 *
 * All state lives in the parent SchedulePage — this is purely presentational.
 * The parent owns ``similarClasses`` (loaded async after the first
 * booking), ``classBasket`` (the multi-class checkout cart), and
 * ``bookedClassName`` (the name shown in the modal title).
 */
export const SimilarClassesDialog = ({
  open,
  bookedClassName,
  similarClasses,
  classBasket,
  basketTotal,
  basketCheckoutLoading,
  basketApplicablePassId,
  onClose,
  onAddToBasket,
  onRemoveFromBasket,
  onJoinWaitlist,
  onCheckout,
}) => {
  if (!open || !similarClasses?.length) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="similar-classes-dialog">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Book More &quot;{bookedClassName}&quot; Classes?</CardTitle>
              <CardDescription>
                Add classes to your basket and pay for them all in one transaction.
              </CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="flex-1 overflow-y-auto space-y-2 pb-4">
          {similarClasses.map((cls) => {
            const inBasket = !!classBasket.find((c) => c.id === cls.id);
            return (
              <div
                key={cls.id}
                className={`p-3 rounded-lg border ${cls.already_booked ? 'bg-muted/50 opacity-60' : inBasket ? 'border-primary bg-primary/5' : 'hover:border-primary/50'}`}
                data-testid={`similar-class-${cls.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex-1">
                    <p className="font-medium">{format(new Date(cls.start_time), 'EEEE, MMMM d')}</p>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {format(new Date(cls.start_time), 'h:mm a')}
                      </span>
                      <span>
                        {cls.spots_available > 0
                          ? `${cls.spots_available} spots left`
                          : 'Full - Waitlist'}
                      </span>
                      {cls.price > 0 && (
                        <span className="font-medium text-foreground">€{Number(cls.price).toFixed(2)}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    {cls.already_booked ? (
                      <Badge variant="outline">Already Booked</Badge>
                    ) : cls.spots_available <= 0 ? (
                      <Button size="sm" variant="outline" onClick={() => onJoinWaitlist(cls)}>
                        Waitlist
                      </Button>
                    ) : inBasket ? (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => onRemoveFromBasket(cls.id)}
                        data-testid={`remove-basket-${cls.id}`}
                      >
                        Remove
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => onAddToBasket(cls)}
                        data-testid={`add-to-basket-${cls.id}`}
                      >
                        Add to Basket
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </CardContent>
        <div className="p-4 border-t bg-muted/30 space-y-3">
          {classBasket.length > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span data-testid="basket-count">
                {classBasket.length} {classBasket.length === 1 ? 'class' : 'classes'} in basket
              </span>
              <span className="font-semibold text-base" data-testid="basket-total">
                {basketApplicablePassId ? 'Covered by ClassPass' : `Total: €${basketTotal.toFixed(2)}`}
              </span>
            </div>
          )}
          {classBasket.length > 0 && basketApplicablePassId && (
            <p className="text-xs text-emerald-700" data-testid="basket-pass-note">
              A ClassPass on file covers all {classBasket.length} classes — no card charge needed.
            </p>
          )}
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>
              {classBasket.length > 0 ? 'Cancel' : 'Done'}
            </Button>
            {classBasket.length > 0 && (
              <Button
                className="flex-1"
                onClick={onCheckout}
                disabled={basketCheckoutLoading}
                data-testid="checkout-basket-btn"
              >
                {basketCheckoutLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : null}
                {basketApplicablePassId
                  ? `Use ClassPass (${classBasket.length})`
                  : basketTotal > 0
                    ? `Checkout €${basketTotal.toFixed(2)}`
                    : 'Book All'}
              </Button>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
